import {
  makeAutoObservable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx"
import { combineLatest, from, ObservableInput, Subscription, timer } from "rxjs"
import { switchMap, tap, retry } from "rxjs/operators"
import { toStream } from "mobx-utils"

import { DataCategoryEntity } from "@framework/types/knowledge"
import knowledgeService from "@services/knowledge.service"
import { searchBy } from "@utils/optionsUtils"

const RETRY_DELAY = 30 * 1000

type State = {
  isLoading: boolean
  data: DataCategoryEntity[]
  total: number
  errorMessage: string
  searchQuery: string
  reloadTrigger: number
}

export class AllDataTypesStore {
  state: State

  private loadingStream$?: Subscription

  constructor() {
    this.state = {
      isLoading: false,

      data: [],

      total: 0,

      searchQuery: "",

      errorMessage: "",

      reloadTrigger: Date.now(),
    }

    makeAutoObservable(this)

    onBecomeObserved(this.state, "total", () => {
      this.loadingStream$ = this.initLoadingStream().subscribe()
    })

    onBecomeUnobserved(this.state, "total", () => {
      this.loadingStream$?.unsubscribe()
    })
  }

  private initLoadingStream = () => {
    const { state } = this

    const loadStream$ = from(
      toStream(() => [state.reloadTrigger], true) as ObservableInput<[boolean]>
    ).pipe(
      tap(() =>
        runInAction(() => {
          state.isLoading = true
          state.errorMessage = ""
        })
      ),
      switchMap(() => knowledgeService.getAllDataTypes$())
    )

    const filterStream$ = from(
      toStream(() => [state.searchQuery], true) as ObservableInput<[string]>
    )

    return combineLatest([loadStream$, filterStream$]).pipe(
      tap(([response, [searchQuery]]) =>
        runInAction(() => {
          const collection = response.data.data ?? []

          state.data = searchBy(collection, searchQuery, (it) => it.name)
          state.total = state.data.length
          state.isLoading = false
        })
      ),
      retry({
        delay: () => {
          runInAction(() => {
            state.errorMessage = "Loading Failed"
          })
          return timer(RETRY_DELAY)
        },
      })
    )
  }

  search = (query: string) => {
    this.state.searchQuery = query
  }

  refresh = () => {
    this.state.reloadTrigger = Date.now()
  }

  get getById() {
    const dataMap = new Map(this.state.data.map((it) => [it.id, it]))
    return (id?: string | null) => {
      if (id == null) return null
      return dataMap.get(id) ?? null
    }
  }

  get getByIndex() {
    const { data } = this.state
    return (index: number) => {
      return data[index]
    }
  }
}

export default AllDataTypesStore
